import { useMemo, useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card } from "@/components/ui/card";
import { calc, DEFAULT_PRICE, PRICE_META, formatEuro, formatKwh } from "@/lib/calc";
import { getPrice } from "@/lib/api";
import { trackSettled } from "@/lib/track";

// Generischer, regelbasierter Stromkostenrechner.
// Steuert sich vollständig über das `device`-Objekt aus data/devices.js.
// Zwei Eingabewege: "Verbrauch bekannt" (exakt) und "bitte schätzen" (geführt).
// Ergebnis immer als Spanne (niedrig / realistisch / hoch), Annahmen sichtbar.

function NumberField({ field, value, onChange }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">
        {field.label}
        {field.unit ? <span className="text-muted-foreground font-normal"> ({field.unit})</span> : null}
      </span>
      <Input
        type="number"
        inputMode="decimal"
        value={value}
        min={field.min}
        max={field.max}
        step={field.step}
        onChange={(e) => onChange(field.id, e.target.value === "" ? "" : Number(e.target.value))}
        data-testid={`calc-field-${field.id}`}
      />
    </label>
  );
}

function PriceField({ value, onChange, meta }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">
        Strompreis <span className="text-muted-foreground font-normal">(€/kWh)</span>
      </span>
      <Input
        type="number"
        inputMode="decimal"
        value={value}
        min={0.05}
        max={2}
        step={0.01}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        data-testid="calc-field-price"
      />
      <span className="text-xs text-muted-foreground">
        Voreingestellt: deutscher Endkundenpreis {meta && meta.period ? `(Stand ${meta.period})` : ""}. Quelle: Eurostat. Bei Bedarf mit deinem eigenen Tarif überschreiben.
      </span>
    </label>
  );
}

// Ergebniszeile pro Zeitraum mit Spanne.
function ResultRow({ label, low, typical, high, strong = false }) {
  return (
    <div className={`flex items-baseline justify-between gap-3 py-2 ${strong ? "" : "border-t border-border/60"}`}>
      <span className={`text-sm ${strong ? "font-semibold" : "text-muted-foreground"}`}>{label}</span>
      <span className="text-right">
        <span className={`tabular-nums ${strong ? "text-2xl font-bold text-primary" : "text-base font-semibold"}`}>
          {formatEuro(typical)}
        </span>
        <span className="block text-xs text-muted-foreground tabular-nums">
          {formatEuro(low)} bis {formatEuro(high)}
        </span>
      </span>
    </div>
  );
}

export function Calculator({ device, defaultMode = "estimate" }) {
  const [mode, setMode] = useState(defaultMode); // "exact" | "estimate"
  const [price, setPriceRaw] = useState(DEFAULT_PRICE);
  const [priceMeta, setPriceMeta] = useState(PRICE_META);
  const priceEditedRef = useRef(false);
  const setPrice = (v) => {
    priceEditedRef.current = true;
    setPriceRaw(v);
  };

  // Aktuellen Endkundenpreis vom Backend ziehen (Eurostat, wöchentlich gecacht).
  // Überschreibt nur, wenn der Nutzer den Preis noch nicht selbst geändert hat.
  useEffect(() => {
    let active = true;
    getPrice().then((p) => {
      if (active && p && !priceEditedRef.current) {
        setPriceRaw(p.pricePerKwh);
        setPriceMeta(p);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  // Exakte Eingabe ------------------------------------------------------
  const [exact, setExact] = useState(() => {
    const o = {};
    device.exact.fields.forEach((f) => (o[f.id] = f.default));
    return o;
  });
  const setExactField = (id, val) => setExact((s) => ({ ...s, [id]: val }));

  // Geführte Schätzung --------------------------------------------------
  const typeField = device.estimate.typeField || null;
  const [typeValue, setTypeValue] = useState(typeField ? typeField.options[0].value : null);
  const [profile, setProfile] = useState(() => {
    const o = {};
    (device.estimate.profileFields || []).forEach((f) => (o[f.id] = f.default));
    return o;
  });
  const setProfileField = (id, val) => setProfile((s) => ({ ...s, [id]: val }));

  // Aktive Verbrauchsspanne (gerätetyp-abhängig oder Default)
  const activeRanges = useMemo(() => {
    if (typeField && typeValue) {
      const opt = typeField.options.find((o) => o.value === typeValue);
      if (opt && opt.ranges) return opt.ranges;
    }
    return device.ranges;
  }, [typeField, typeValue, device.ranges]);

  const p = price === "" ? 0 : price;

  // Berechnung ----------------------------------------------------------
  const result = useMemo(() => {
    const build = (consumption) => {
      switch (device.mode) {
        case "watt":
          return {
            watt: consumption,
            hoursPerDay: mode === "exact" ? num(exact.hoursPerDay) : num(profile.hoursPerDay),
            daysPerWeek: mode === "exact" ? num(exact.daysPerWeek) : num(profile.daysPerWeek),
            price: p,
          };
        case "kwh_per_cycle":
          return {
            kwhPerCycle: consumption,
            cyclesPerWeek: mode === "exact" ? num(exact.cyclesPerWeek) : num(profile.cyclesPerWeek),
            price: p,
          };
        case "kwh_per_year":
          return { kwhPerYear: consumption, price: p };
        default:
          return {};
      }
    };

    if (mode === "exact") {
      // Exakt: ein einzelner Wert, als enge symbolische Spanne dargestellt.
      const value = exactConsumption(device, exact);
      const r = calc(device.mode, build(value));
      return { low: r, typical: r, high: r, single: true, consumption: value };
    }

    // Schätzung: drei Verbrauchswerte aus der aktiven Spanne.
    return {
      low: calc(device.mode, build(activeRanges.low)),
      typical: calc(device.mode, build(activeRanges.typical)),
      high: calc(device.mode, build(activeRanges.high)),
      single: false,
      ranges: activeRanges,
    };
  }, [mode, exact, profile, activeRanges, p, device]);

  const unit = device.rangeUnit;

  // Tracking: calculator_used + result_shown, debounced so rapid slider moves collapse
  useEffect(() => {
    trackSettled("calculator_used", { calculator: "device_cost", mode });
    trackSettled("result_shown", { calculator: "device_cost", verdict: "ok" });
  }, [result, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Card className="p-4 sm:p-6" data-testid="calculator">
      <div data-testid="calculator-modes">
        {mode === "estimate" ? (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              {typeField ? (
                <div className="sm:col-span-2">
                  <span className="text-sm font-medium">{typeField.label}</span>
                  <RadioGroup
                    value={typeValue}
                    onValueChange={setTypeValue}
                    className="mt-2 grid gap-2 sm:grid-cols-2"
                    data-testid="calc-type"
                  >
                    {typeField.options.map((opt) => (
                      <label
                        key={opt.value}
                        className="flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer hover:bg-muted/50 has-[:checked]:border-primary has-[:checked]:bg-primary/5"
                      >
                        <RadioGroupItem value={opt.value} />
                        <span className="text-sm">{opt.label}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              ) : null}

              {(device.estimate.profileFields || []).map((f) => (
                <NumberField key={f.id} field={f} value={profile[f.id]} onChange={setProfileField} />
              ))}
              <PriceField value={price} onChange={setPrice} meta={priceMeta} />
            </div>
            <button
              type="button"
              onClick={() => setMode("exact")}
              className="mt-4 text-sm text-primary underline underline-offset-2 hover:no-underline"
              data-testid="calc-switch-exact"
            >
              Ich kenne meinen genauen Verbrauch
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setMode("estimate")}
              className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              data-testid="calc-switch-estimate"
            >
              ← Zurück zur Schätzung
            </button>
            <div className="grid gap-4 sm:grid-cols-2">
              {device.exact.fields.map((f) => (
                <NumberField key={f.id} field={f} value={exact[f.id]} onChange={setExactField} />
              ))}
              <PriceField value={price} onChange={setPrice} meta={priceMeta} />
            </div>
          </>
        )}
      </div>

      {/* Ergebnis */}
      <div className="mt-6 rounded-xl bg-muted/40 p-4 sm:p-5" data-testid="calc-result">
        <div className="text-sm font-medium text-muted-foreground">
          Geschätzte Stromkosten {result.single ? "" : "(niedrig bis hoch)"}
        </div>
        <div className="mt-2">
          <ResultRow strong label="Pro Monat" low={result.low.perMonth} typical={result.typical.perMonth} high={result.high.perMonth} />
          {device.mode === "kwh_per_cycle" ? (
            <ResultRow label="Pro Durchgang" low={result.low.perUse} typical={result.typical.perUse} high={result.high.perUse} />
          ) : (
            <ResultRow label="Pro Tag" low={result.low.perDay} typical={result.typical.perDay} high={result.high.perDay} />
          )}
          <ResultRow label="Pro Woche" low={result.low.perWeek} typical={result.typical.perWeek} high={result.high.perWeek} />
          <ResultRow label="Pro Jahr" low={result.low.perYear} typical={result.typical.perYear} high={result.high.perYear} />
          <ResultRow
            label="Verbrauch pro Jahr"
            low={result.low.kwhPerYear}
            typical={result.typical.kwhPerYear}
            high={result.high.kwhPerYear}
          />
        </div>

        {/* Annahmen, sichtbar (PRD §17) */}
        <div className="mt-4 rounded-lg border border-dashed bg-background/60 p-3 text-xs text-muted-foreground leading-relaxed" data-testid="calc-assumptions">
          <strong className="text-foreground">Annahme: </strong>
          {result.single ? (
            <>Eingegebener Verbrauch, {fmtNum(p)} €/kWh. Werte oben kannst du jederzeit anpassen.</>
          ) : (
            <>
              {describeRange(device, result.ranges, unit)} bei {fmtNum(p)} €/kWh.{" "}
              {device.estimate.assumptionText} Du kannst auf „Verbrauch bekannt“ wechseln und eigene Werte eintragen.
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

// Hilfen ----------------------------------------------------------------

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function fmtNum(n) {
  return new Intl.NumberFormat("de-DE", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n);
}

// Liest den maßgeblichen Verbrauchswert aus der exakten Eingabe.
function exactConsumption(device, exact) {
  switch (device.mode) {
    case "watt":
      return num(exact.watt);
    case "kwh_per_cycle":
      return num(exact.kwhPerCycle);
    case "kwh_per_year":
      return num(exact.kwhPerYear);
    default:
      return 0;
  }
}

function describeRange(device, ranges, unit) {
  return `${fmtNum(ranges.low)} bis ${fmtNum(ranges.high)} ${unit} (realistisch ${fmtNum(ranges.typical)} ${unit})`;
}

// re-export, damit Seiten den Default kennen
export { formatEuro, formatKwh };
